import React, { ChangeEvent, FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AssignmentTaskTypes } from '../../../../config/api/models/Assignment';
import ExerciseSeriesMultiSelect from './ExerciseSeriesMultiSelect';
import { useFetch } from '../../../shared/hooks';
import { ExerciseSeriesInfo } from '../../../../config/api/models/Exercise';
import apiRoutes from '../../../../config/api/routes';
import { StyledCheckboxFormControlLabel } from '../../../shared/components';
import { ITrajectoryDetail } from '../../../../config/api/models/Trajectory';
import { Checkbox, styled } from '@material-ui/core';

type Props = {
  trajectory: ITrajectoryDetail;
  value: { taskTypes: AssignmentTaskTypes; seriesIds: string[] };
  onChange: (value: { taskTypes: AssignmentTaskTypes; seriesIds: string[] }) => void;
};

const Wrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const TaskTypeSelect: FC<Props> = ({ trajectory, value: { taskTypes, seriesIds }, onChange }) => {
  const { t } = useTranslation();

  const [seriesResponse, getSeries] = useFetch<ExerciseSeriesInfo[]>(
    apiRoutes.pages(trajectory.id!, 'exercises')
  );
  useEffect(() => {
    getSeries();
  }, [getSeries]);
  const seriesOptions = useMemo(() => {
    if (!seriesResponse.success) return [];
    return seriesResponse.data.map(({ id, title }) => ({ id, label: title }));
  }, [seriesResponse]);

  const handleChangeSeriesIds = useCallback(
    (seriesIds: string[]) => onChange({ taskTypes, seriesIds }),
    [onChange, taskTypes]
  );

  useEffect(() => {
    if (taskTypes.full_trajectory) {
      onChange({
        taskTypes: { full_trajectory: true, framework: false, theory: false, series: false },
        seriesIds: [],
      });
    }
  }, [taskTypes.full_trajectory, onChange, seriesOptions]);

  const handleChangeTaskType =
    (taskType: keyof AssignmentTaskTypes) => (event: ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked as boolean;

      let newTaskTypes = {};
      if (taskType === 'full_trajectory' && checked) {
        newTaskTypes = { full_trajectory: true, framework: false, theory: false, series: false };
      } else {
        newTaskTypes = { ...taskTypes, full_trajectory: false, [taskType]: checked };
      }

      if (taskType === 'full_trajectory' || (taskType === 'series' && !checked)) {
        onChange({ taskTypes: newTaskTypes, seriesIds: [] });
      } else {
        onChange({ taskTypes: newTaskTypes, seriesIds });
      }
    };

  return (
    <Wrapper>
      <StyledCheckboxFormControlLabel
        control={
          <Checkbox
            checked={taskTypes.full_trajectory}
            onChange={handleChangeTaskType('full_trajectory')}
            color="primary"
          />
        }
        labelPlacement="end"
        label={t('trajectory.sidebar.assignments.form.completeTrajectory')}
      />
      {trajectory.pages.some(page => page.type === 'opbouw' && page.hasContent) && (
        <StyledCheckboxFormControlLabel
          control={
            <Checkbox
              checked={taskTypes.framework}
              onChange={handleChangeTaskType('framework')}
              color="primary"
            />
          }
          labelPlacement="end"
          label={t('trajectory.sidebar.assignments.form.opbouw')}
          disabled={taskTypes.full_trajectory}
        />
      )}
      {trajectory.pages.some(page => page.type === 'theory' && page.hasContent) && (
        <StyledCheckboxFormControlLabel
          control={
            <Checkbox
              checked={taskTypes.theory}
              onChange={handleChangeTaskType('theory')}
              color="primary"
            />
          }
          labelPlacement="end"
          label={t('trajectory.sidebar.assignments.form.theory')}
          disabled={taskTypes.full_trajectory}
        />
      )}
      {trajectory.pages.some(page => page.type === 'worksheet_exercises' && page.hasContent) && (
        <>
          <StyledCheckboxFormControlLabel
            control={
              <Checkbox
                checked={taskTypes.series}
                onChange={handleChangeTaskType('series')}
                color="primary"
              />
            }
            labelPlacement="end"
            label={t('trajectory.sidebar.assignments.form.exerciseSeries')}
            disabled={taskTypes.full_trajectory}
          />
          <ExerciseSeriesMultiSelect
            trajectoryId={trajectory.id}
            value={seriesIds}
            onChange={handleChangeSeriesIds}
            options={seriesOptions}
            disabled={!taskTypes.series || taskTypes.full_trajectory}
            required={taskTypes.series}
          />
        </>
      )}
    </Wrapper>
  );
};

export default TaskTypeSelect;
